import { CircleUser, Edit, EllipsisVertical, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../components/ui/button.tsx';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu.tsx';
import Text from '../../../components/ui/text.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useAppState } from '../../../lib/hooks/use-app-state.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CrmCommentDTO } from '../../../server/dto/crm-comment-dto.ts';
import { api } from '../../../utils/api.ts';
import { DialogStateManager } from '../../dialog/dialog-provider.tsx';
import { RichTextForm } from '../../rich-text/components/rich-text-form.tsx';
import type { RichTextValue } from '../../rich-text/components/rich-text-input.tsx';
import { RichTextViewer } from '../../rich-text/components/rich-text-viewer.tsx';
import { FromNow } from '../../shared/components/from-now/from-now.tsx';
import { DeleteCommentDialog } from './delete-comment-dialog.tsx';

type CommentItemProps = {
    comment: CrmCommentDTO;
};

export const CommentItem = ({ comment }: CommentItemProps) => {
    const { t } = useLocale('tasks');

    const { toast } = useToast();
    const appState = useAppState();
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState<RichTextValue | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const { commentId, createdBy, createdAt, updatedAt, contentRich } = comment;
    const userDisplayName = `${createdBy.firstName} ${createdBy.lastName}`.trim();

    // Check if current user can edit/delete this comment
    const canEditDelete = appState.platoUser.id === createdBy.id;
    const isEdited = updatedAt.getTime() !== createdAt.getTime();

    const apiUtils = api.useUtils();

    const updateCommentMutation = api.platoUser.command.comments.update.useMutation({
        onSuccess: (updatedComment) => {
            const key = comment.taskId
                ? { taskId: comment.taskId }
                : comment.noteId
                  ? { noteId: comment.noteId }
                  : undefined;
            if (!key) {
                return;
            }
            apiUtils.platoUser.query.comments.getManyComments.setData(key, (oldData) => {
                if (!oldData) {
                    return [];
                }
                return oldData.map((c) => (c.commentId === comment.commentId ? updatedComment : c));
            });

            setIsEditing(false);
            setEditContent(null);
            toast({
                icon: 'success',
                title: t('comments__toast__updated_success_title'),
            });
        },
        onError: (error) => {
            toast({
                icon: 'warning',
                title: 'Error',
                description: error.message,
            });
        },
    });

    const handleEdit = () => {
        setEditContent(contentRich);
        setIsEditing(true);
        setDropdownOpen(false);
    };

    const handleSaveEdit = (content: RichTextValue) => {
        updateCommentMutation.mutate({
            commentId,
            contentRich: content,
        });
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditContent(null);
    };

    const handleDelete = () => {
        void DialogStateManager.show(DeleteCommentDialog, {
            comment,
        });
        setDropdownOpen(false);
    };

    return (
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                    <CircleUser className="h-4 w-4 text-neutral-600 shrink-0" />
                    <Text color="neutral-700">
                        {userDisplayName} {<FromNow date={createdAt} minimumUnit="minute" />}
                        {isEdited && <span className="ml-1">{t('comments__edited')}</span>}
                    </Text>
                </div>

                {canEditDelete && (
                    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-neutral-400 hover:text-neutral-600"
                            >
                                <EllipsisVertical className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" side="bottom">
                            <DropdownMenuItem onSelect={handleEdit} disabled={isEditing}>
                                <Edit className="h-4 w-4" />
                                <span>{t('comments__edit')}</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={handleDelete}>
                                <Trash2 className="h-4 w-4 text-red-700" />
                                <span className="text-red-700">{t('comments__delete')}</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </div>

            <div className="mt-3">
                {isEditing ? (
                    <RichTextForm
                        trackingContext={{ source: 'comment' }}
                        placeholder={t('comments__add_placeholder')}
                        submitTitle={t('comments__save')}
                        onSubmit={handleSaveEdit}
                        onCancel={handleCancelEdit}
                        isSubmitting={updateCommentMutation.isPending}
                        initialValue={editContent}
                    />
                ) : (
                    <div className="text-sm text-neutral-700 prose prose-sm max-w-none prose-p:my-1 prose-p:leading-relaxed">
                        <RichTextViewer
                            content={contentRich}
                            key={`comment-${commentId}-${updatedAt.toISOString()}`}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
